import React, { useEffect, useState } from 'react';
import { assistantAPI } from '../../services/assistant';
import '../../styles/Assistant.css';

/** US dollars: cents shown, and amounts under a cent shown as such rather than as $0.00. */
export function formatUsd(value) {
  const n = Number(value) || 0;
  if (n > 0 && n < 0.01) return '<$0.01';
  return `$${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
const formatTokens = (n) => (Number(n) || 0).toLocaleString('en-GB');
const monthName = (ym) => {
  const [y, m] = String(ym).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};
const describeError = (err) => err?.response?.data?.error || err?.message || 'Could not read the usage';

/**
 * Company admins: the AI assistant's requests to OpenAI, their tokens and
 * estimated cost, by month and, for one month, by person.
 */
export default function AssistantUsage({ companyId }) {
  const [month, setMonth] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [capInput, setCapInput] = useState('');
  const [savingCap, setSavingCap] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let current = true;
    assistantAPI.usage(companyId, month)
      .then((r) => { if (current) { setReport(r.data); setCapInput(String(r.data.cap?.capUsd ?? '')); setError(''); } })
      .catch((err) => { if (current) setError(describeError(err)); });
    return () => { current = false; };
  }, [companyId, month, reload]);

  const saveCap = async (value) => {
    setSavingCap(true);
    try {
      await assistantAPI.setCap(companyId, value);
      setReload((n) => n + 1);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSavingCap(false);
    }
  };

  if (!report) return error ? <div className="error-message" role="alert">{error}</div> : null;
  const t = report.totals;
  const cap = report.cap || null;
  const share = cap?.capUsd ? Math.min(100, Math.round((t.estimatedCostUsd / cap.capUsd) * 100)) : 0;
  const isThisMonth = report.month === new Date().toISOString().slice(0, 7);
  const months = report.byMonth.some((m) => m.month === report.month) ? report.byMonth : [{ month: report.month }, ...report.byMonth];
  const prices = Object.entries(report.pricesPerMillionTokens || {});

  return (
    <div className="ai-usage">
      <div className="ai-usage-head">
        <h4>Usage and cost</h4>
        <label>
          <span className="sr-only">Month</span>
          <select aria-label="Month" value={report.month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => <option key={m.month} value={m.month}>{monthName(m.month)}</option>)}
          </select>
        </label>
      </div>

      <dl className="ai-usage-tiles">
        <div>
          <dt>Estimated cost</dt>
          <dd>{formatUsd(t.estimatedCostUsd)}</dd>
          {isThisMonth && cap && (
            <span className={`ai-usage-limit ${share >= 100 ? 'ai-usage-limit--reached' : ''}`}>
              <span className="ai-usage-bar" aria-hidden="true"><span style={{ width: `${share}%` }} /></span>
              {share >= 100 ? 'The monthly limit is reached: the AI assistant is paused until you raise it or the month ends.' : `${share}% of the monthly limit of ${formatUsd(cap.capUsd)}`}
            </span>
          )}
        </div>
        <div><dt>Tokens</dt><dd>{formatTokens(t.totalTokens)}</dd></div>
        <div><dt>Conversations</dt><dd>{formatTokens(t.conversations)}</dd></div>
        <div><dt>Requests to OpenAI</dt><dd>{formatTokens(t.requests)}</dd></div>
      </dl>

      {cap && (
        <form className="ai-usage-cap" onSubmit={(e) => { e.preventDefault(); saveCap(capInput); }}>
          <label>
            Monthly spending limit (US dollars)
            <span className="ai-usage-cap-row">
              <span aria-hidden="true">$</span>
              <input type="number" min="1" step="1" max={cap.maxUsd} value={capInput} disabled={savingCap} onChange={(e) => setCapInput(e.target.value)} aria-label="Monthly spending limit in US dollars" />
              <button type="submit" className="ai-button ai-button--primary" disabled={savingCap || !capInput || Number(capInput) === cap.capUsd}>Save limit</button>
              {cap.chosenUsd !== null && <button type="button" className="ai-link" disabled={savingCap} onClick={() => saveCap(null)}>Use the default ({formatUsd(cap.defaultUsd)})</button>}
            </span>
          </label>
          <small>When this month&apos;s estimated cost reaches the limit, the AI assistant pauses until the next month or until you raise it. Up to {formatUsd(cap.maxUsd)}.</small>
        </form>
      )}

      {report.byModel?.length > 0 && (
        <div className="datasets-table-container">
          <table className="datasets-table ai-usage-table">
            <caption>{monthName(report.month)} by model and effort</caption>
            <thead><tr><th>Model</th><th>Effort</th><th>Requests</th><th>Input tokens</th><th>Of which cached</th><th>Output tokens</th><th>Estimated cost</th></tr></thead>
            <tbody>
              {report.byModel.map((m) => (
                <tr key={`${m.model}|${m.reasoningEffort}`}>
                  <td>{m.modelLabel}</td>
                  <td>{m.reasoningEffort ? m.reasoningEffort.replace(/^./, (c) => c.toUpperCase()) : 'Not recorded'}</td>
                  <td>{formatTokens(m.requests)}</td>
                  <td>{formatTokens(m.inputTokens)}</td>
                  <td>{formatTokens(m.cachedTokens)}</td>
                  <td>{formatTokens(m.outputTokens)}</td>
                  <td>{formatUsd(m.estimatedCostUsd)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={2}>Total</th>
                <td>{formatTokens(t.requests)}</td>
                <td>{formatTokens(t.inputTokens)}</td>
                <td>{formatTokens(t.cachedTokens)}</td>
                <td>{formatTokens(t.outputTokens)}</td>
                <td>{formatUsd(t.estimatedCostUsd)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {report.byPerson.length > 0 ? (
        <div className="datasets-table-container">
          <table className="datasets-table ai-usage-table">
            <caption>{monthName(report.month)} by person</caption>
            <thead><tr><th>Person</th><th>Conversations</th><th>Input tokens</th><th>Of which cached</th><th>Output tokens</th><th>Estimated cost</th></tr></thead>
            <tbody>
              {report.byPerson.map((p) => (
                <tr key={p.userId ?? 'removed'}>
                  <td>{p.name}</td>
                  <td>{formatTokens(p.conversations)}</td>
                  <td>{formatTokens(p.inputTokens)}</td>
                  <td>{formatTokens(p.cachedTokens)}</td>
                  <td>{formatTokens(p.outputTokens)}</td>
                  <td>{formatUsd(p.estimatedCostUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="settings-help">The AI assistant was not used in {monthName(report.month)}.</p>}

      {report.byMonth.length > 1 && (
        <div className="datasets-table-container">
          <table className="datasets-table ai-usage-table">
            <caption>The last 12 months</caption>
            <thead><tr><th>Month</th><th>Conversations</th><th>Tokens</th><th>Estimated cost</th></tr></thead>
            <tbody>
              {report.byMonth.map((m) => (
                <tr key={m.month}>
                  <td>{monthName(m.month)}</td>
                  <td>{formatTokens(m.conversations)}</td>
                  <td>{formatTokens(m.totalTokens)}</td>
                  <td>{formatUsd(m.estimatedCostUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="settings-help">
        Costs are estimates in US dollars, worked out from OpenAI&apos;s list prices when each request was made
        {prices.length > 0 && <> ({prices.map(([model, p]) => `${model}: $${p.input} per million input tokens, $${p.cachedInput} cached, $${p.output} output`).join('; ')})</>}.
        Cached input is text OpenAI has seen recently, such as the AI assistant&apos;s instructions, and costs less. OpenAI&apos;s invoice is the final figure.
        {t.unpricedRequests > 0 && ` ${formatTokens(t.unpricedRequests)} requests used a model with no known price and are not included in the cost.`}
      </p>
      {error && <div className="error-message" role="alert">{error}</div>}
    </div>
  );
}
